import { App } from "antd";
import { useEffect, useState } from "react";
import {
    getRouteFindAllByPointId,
    getSchedulerNextVisitDatesByPoint,
} from "tm-shared/api";

interface UseAvailableDatesResult {
    dates: { [roleSlug: string]: string[] };
    roles: string[];
    isLoading: boolean;
}

export function useAvailableDates(
    pointId: string | undefined,
): UseAvailableDatesResult {
    const { notification } = App.useApp();

    const [dates, setDates] = useState<{ [roleSlug: string]: string[] }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [roles, setRoles] = useState<string[]>([]);

    useEffect(() => {
        // Не робимо запит якщо маркет ще не вибраний
        if (!pointId) {
            setDates({});
            setRoles([]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const fetchDates = async () => {
            try {
                setIsLoading(true);

                const pointRoutes = await getRouteFindAllByPointId({
                    pointId,
                });

                if (cancelled) return;

                const roleSlugs = pointRoutes.result.map((route) => route.role);

                setRoles(roleSlugs);

                const datePromises = pointRoutes.result.map(async (route) => {
                    try {
                        const result = await getSchedulerNextVisitDatesByPoint({
                            pointId,
                            routeId: route.uuid,
                        });

                        return {
                            roleSlug: route.role,
                            dates: result.result,
                        };
                    } catch (error) {
                        console.error(
                            `Failed to fetch dates for role ${route.role}:`,
                            error,
                        );
                        return { roleSlug: route.role, dates: [] };
                    }
                });

                const results = await Promise.all(datePromises);

                if (cancelled) return;

                const datesMap = results.reduce(
                    (acc, { roleSlug, dates }) => {
                        acc[roleSlug] = dates;
                        return acc;
                    },
                    {} as { [roleSlug: string]: string[] },
                );

                setDates(datesMap);
            } catch (error) {
                if (!cancelled) {
                    console.error("Failed to fetch available dates:", error);
                    notification.error({
                        title: "Помилка завантаження доступних дат",
                    });
                    setDates({});
                    setRoles([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchDates();

        return () => {
            cancelled = true;
        };
    }, [notification, pointId]);

    return { dates, roles, isLoading };
}
